import { isRecord, validatedJson } from "@/lib/ui/validatedJson";

import type {
  PublishedShowArtifacts,
  SavedPublishedShowArtifacts,
} from "@/lib/shows/publicShowArtifacts";

export type {
  PublishedShowArtifacts,
  SavedPublishedShowArtifacts,
} from "@/lib/shows/publicShowArtifacts";

function isSavedPublishedShowArtifacts<Details, Roster, Plan, Assignments>(
  value: unknown,
): value is SavedPublishedShowArtifacts<Details, Roster, Plan, Assignments> {
  return isRecord(value) && typeof value.showId === "string" && value.state === "published"
    && typeof value.savedAt === "string" && "details" in value && "roster" in value
    && "plan" in value && "assignments" in value;
}

const CACHE_NAME = "trovie-public-show-artifacts-v1";
const CACHE_PATH = "/__trovie-cache__/shows/";

function key(showId: string): string {
  return new URL(`${CACHE_PATH}${encodeURIComponent(showId)}`, window.location.origin).href;
}

export async function savePublishedShowArtifacts(
  artifacts: PublishedShowArtifacts,
): Promise<boolean> {
  if (!("caches" in window)) return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    const saved: SavedPublishedShowArtifacts = {
      ...artifacts,
      savedAt: new Date().toISOString(),
    };
    await cache.put(
      key(artifacts.showId),
      new Response(JSON.stringify(saved), { headers: { "content-type": "application/json" } }),
    );
    return true;
  } catch {
    return false;
  }
}

export async function readPublishedShowArtifacts<
  Details = unknown,
  Roster = unknown,
  Plan = unknown,
  Assignments = unknown,
>(
  showId: string,
): Promise<SavedPublishedShowArtifacts<Details, Roster, Plan, Assignments> | null> {
  if (!("caches" in window)) return null;
  try {
    const response = await (await caches.open(CACHE_NAME)).match(key(showId));
    if (!response) return null;
    const saved = await validatedJson(
      response,
      isSavedPublishedShowArtifacts<Details, Roster, Plan, Assignments>,
      "Cached Show artifacts were invalid.",
    );
    return saved.showId === showId && saved.state === "published" && saved.savedAt
      ? saved
      : null;
  } catch {
    return null;
  }
}

export async function deletePublishedShowArtifacts(showId: string): Promise<boolean> {
  if (!("caches" in window)) return false;
  try {
    return await (await caches.open(CACHE_NAME)).delete(key(showId));
  } catch {
    return false;
  }
}
